import { z } from 'zod';
import { CASH_DRAWER_KINDS, CREDENTIAL_TYPES, MANUAL_PAYMENT_METHODS } from '@bulk/types';
import { accessDecisionSchema, accessEventSchema } from './access';
import { sessionPackageSchema } from './appointments';
import { bookingSchema, classPackSchema } from './bookings';
import { sessionSchema } from './classes';
import { dateTimeSchema, idSchema } from './common';
import { createCredentialSchema, createMemberSchema, credentialSchema, memberLoginSchema, memberSchema } from './members';
import { membershipSchema } from './memberships';
import { paymentSchema } from './payments';
import { walletSchema } from './wallet';

export const receptionLookupQuerySchema = z.object({
  q: z.string().trim().min(1).max(120),
  credentialType: z.enum(CREDENTIAL_TYPES).default('RFID_CARD'),
});
export type ReceptionLookupQuery = z.infer<typeof receptionLookupQuerySchema>;

export const receptionMemberSummarySchema = z.object({
  member: memberSchema,
  membership: membershipSchema.nullable(),
  lastVisitAt: z.string().nullable(),
  credentialCount: z.number().int(),
  matchedBy: z.enum(['CREDENTIAL', 'SEARCH']),
});
export type ReceptionMemberSummaryDto = z.infer<typeof receptionMemberSummarySchema>;

export const receptionLookupResultSchema = z.object({
  items: z.array(receptionMemberSummarySchema),
  matchedCredential: z.boolean(),
});
export type ReceptionLookupResultDto = z.infer<typeof receptionLookupResultSchema>;

export const receptionOverviewSchema = z.object({
  member: memberSchema,
  birthdayToday: z.boolean().describe("It is the member's birthday in the gym's time zone, while the gym greets members"),
  membership: membershipSchema.nullable(),
  memberships: z.array(membershipSchema),
  credentials: z.array(credentialSchema),
  todaysBookings: z.array(bookingSchema),
  recentVisits: z.array(accessEventSchema),
  lastVisitAt: z.string().nullable(),
  visitsToday: z.number().int(),
  pendingPaymentCount: z.number().int(),
  login: memberLoginSchema,
  wallet: walletSchema.nullable().describe('The prepaid balance; null without wallet.read'),
  sessionPackages: z.array(sessionPackageSchema).describe('Session packages with sessions left that are still valid'),
  classPacks: z.array(classPackSchema).describe('Class packs with visits left that are still valid'),
});
export type ReceptionOverviewDto = z.infer<typeof receptionOverviewSchema>;

export const checkInSchema = z.object({
  memberId: idSchema,
  readerId: idSchema.optional(),
  override: z.boolean().default(false),
  note: z.string().trim().max(300).optional(),
});
export type CheckInInput = z.infer<typeof checkInSchema>;

export const checkInResultSchema = accessDecisionSchema.extend({
  override: z.boolean(),
  visitsToday: z.number().int(),
});
export type CheckInResultDto = z.infer<typeof checkInResultSchema>;

export const deskSaleSchema = z
  .object({
    memberId: idSchema.optional(),
    newMember: createMemberSchema.optional(),
    planId: idSchema,
    method: z.enum(MANUAL_PAYMENT_METHODS),
    startsAt: dateTimeSchema.optional(),
    note: z.string().trim().max(500).optional(),
    credential: createCredentialSchema.optional(),
    termsAccepted: z.boolean().default(false),
    soldByUserId: idSchema.optional().describe('The staff member credited with the sale, from `GET /targets/sellers`; the caller when left out'),
    promoCode: z.string().trim().min(1).max(32).optional().describe('A promo code the member brought; 422 PROMO_CODE_INVALID with a reason when it gives nothing'),
  })
  .refine((v) => Boolean(v.memberId) !== Boolean(v.newMember), { message: 'Provide either memberId or newMember' });
export type DeskSaleInput = z.infer<typeof deskSaleSchema>;

export const deskSaleResultSchema = z.object({
  member: memberSchema,
  membership: membershipSchema.nullable().describe('The membership the sale started; null for a session package'),
  sessionPackage: sessionPackageSchema.nullable().describe('The sessions a session-package sale granted; null for a membership'),
  classPack: classPackSchema.nullable().describe('The visits a class-pack sale granted; null otherwise'),
  payment: paymentSchema,
  credential: credentialSchema.nullable(),
  createdMember: z.boolean(),
  acceptedDocuments: z.array(z.string()),
  login: memberLoginSchema,
});
export type DeskSaleResultDto = z.infer<typeof deskSaleResultSchema>;

export const receptionTodaySchema = z.object({
  date: z.string(),
  timezone: z.string(),
  currency: z.string(),
  checkInsToday: z.number().int(),
  deniedToday: z.number().int(),
  salesTodayMinor: z.number().int(),
  salesTodayCount: z.number().int(),
  sessions: z.array(sessionSchema),
  expiringSoon: z.array(
    z.object({
      memberId: idSchema,
      memberName: z.string(),
      planName: z.string(),
      endsAt: z.string(),
    }),
  ),
  recentCheckIns: z.array(accessEventSchema.extend({ birthdayToday: z.boolean().describe("It is the member's birthday in the gym's time zone, while the gym greets members") })),
});
export type ReceptionTodayDto = z.infer<typeof receptionTodaySchema>;

export const openCashShiftSchema = z.object({
  openingFloatMinor: z.number().int().min(0).max(100_000_000),
});
export type OpenCashShiftInput = z.infer<typeof openCashShiftSchema>;

export const closeCashShiftSchema = z.object({
  countedCashMinor: z.number().int().min(0).max(100_000_000),
  note: z.string().trim().max(500).optional(),
});
export type CloseCashShiftInput = z.infer<typeof closeCashShiftSchema>;

export const cashShiftSchema = z.object({
  id: idSchema,
  kind: z.enum(CASH_DRAWER_KINDS).describe("DESK is reception's drawer; BAR is the bar's own, when the gym keeps bar cash apart"),
  locationId: idSchema,
  locationName: z.string(),
  status: z.enum(['OPEN', 'CLOSED']),
  currency: z.string(),
  openedAt: z.string(),
  openedByName: z.string().nullable(),
  openingFloatMinor: z.number().int(),
  closedAt: z.string().nullable(),
  closedByName: z.string().nullable(),
  cashSalesMinor: z.number().int().nullable(),
  cashRefundsMinor: z.number().int().nullable(),
  cashPaidOutMinor: z.number().int().nullable().describe('Expenses paid from this drawer during the shift, voided ones left out'),
  expectedCashMinor: z.number().int().nullable(),
  countedCashMinor: z.number().int().nullable(),
  differenceMinor: z.number().int().nullable(),
  note: z.string().nullable(),
});
export type CashShiftDto = z.infer<typeof cashShiftSchema>;

export const currentCashShiftSchema = z.object({
  shift: cashShiftSchema.nullable(),
  requireOpenShift: z.boolean(),
  barDrawer: z.boolean().describe('Whether the bar keeps its own drawer at this gym'),
});
export type CurrentCashShiftDto = z.infer<typeof currentCashShiftSchema>;

export const deskPrinterSchema = z.object({
  available: z.boolean(),
  name: z.string().nullable(),
  online: z.boolean(),
  lastSeenAt: z.string().nullable(),
});
export type DeskPrinterDto = z.infer<typeof deskPrinterSchema>;

export const printJobSchema = z.object({
  id: idSchema,
  status: z.enum(['PENDING', 'SENT', 'DONE', 'FAILED']),
  message: z.string().nullable(),
  createdAt: z.string(),
  ackedAt: z.string().nullable(),
});
export type PrintJobDto = z.infer<typeof printJobSchema>;

